import { useEffect, useRef, useCallback } from 'react';
import { HOME, ORDER, MY } from '@/constants/path';
import { getUnionPaySDK } from '@/channels/sdk';

const canIUseDoc = typeof document !== 'undefined';

// TODO 小程序默认名称待确认
// webview 方案 title 为空 会展示 URL -> 不能为空
const DEFAULT_DOC_TITLE = '麦当劳';

export interface UseTitleOptions {
  restoreOnUnmount?: boolean;
}

const DEFAULT_USE_TITLE_OPTIONS: UseTitleOptions = {
  restoreOnUnmount: false,
};

/**
 * For H5
 * @param title
 * @param options
 */
function useTitle(title: string, options: UseTitleOptions = DEFAULT_USE_TITLE_OPTIONS) {
  const prevTitleRef = useRef(canIUseDoc ? document.title : '');

  const updateTitle = useCallback(
    (name?: string) => {
      const _title = name != null ? name : title;
      if (canIUseDoc && document.title !== _title) {
        document.title = _title;
      }
    },
    [title],
  );

  useEffect(() => {
    updateTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  useEffect(() => {
    if (options && options.restoreOnUnmount) {
      const _title = prevTitleRef.current;
      return () => {
        document.title = _title;
      };
    } else {
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return updateTitle;
}

export type UnionPaySDKTitleStyleOptions = Parameters<UpsdkType['setTitleStyle']>[0] & {
  title?: string;
};

/**
 *
 * @param options
 */
export function useUnionPaySDKTitleStyle(options?: UnionPaySDKTitleStyleOptions) {
  const setStyles = useCallback(
    (o?: UnionPaySDKTitleStyleOptions) => {
      const _options = o || options || {};
      const { title = '', ...styles } = _options;

      getUnionPaySDK()?.setNavigationBarTitle({ title });
      getUnionPaySDK()?.setTitleStyle(styles);
    },
    [options],
  );

  const setStylesRef = useRef(setStyles);
  useEffect(() => {
    setStylesRef.current = setStyles;
  }, [setStyles]);

  useEffect(() => {
    setStylesRef.current();
  }, []);

  return setStyles;
}

/**
 * legacy useTitle
 * @deprecated Use `useAppTitle` instead
 */
export const useAppHomeTitle = (title: string, bgColor?: string) => {
  useTitle(title ?? DEFAULT_DOC_TITLE);

  useEffect(() => {
    getUnionPaySDK()?.setNavigationBarTitle({ title });
  }, [title]);

  useEffect(() => {
    getUnionPaySDK()?.registerLifecycle({
      onAppShow: function () {
        getUnionPaySDK()?.setNavigationBarTitle({ title });
      },
      onPageShow: function () {
        getUnionPaySDK()?.setNavigationBarTitle({ title });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isHideBackBtn = [HOME, ORDER, MY].includes(location.pathname);

    getUnionPaySDK()?.setTitleStyle({
      backBtnVisible: isHideBackBtn ? '0' : '1', // 可选，左侧返回按钮是否显示。'0'不显示，'1'显示，不传或空则默认显示
      navBackgroundColor: bgColor || '0x8FFFFFFF',
      appletStyle: 'black',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export type UseAppTitleOptions = UnionPaySDKTitleStyleOptions &
  UseTitleOptions & {
    title?: string;
  };

const useAppTitle = (options?: UseAppTitleOptions) => {
  const { restoreOnUnmount, ...rest } = options || {};

  const updateTitle = useTitle(rest.title ?? DEFAULT_DOC_TITLE, { restoreOnUnmount });
  const updateUnionTitleStyle = useUnionPaySDKTitleStyle(rest);

  const updateAll = useCallback(
    (o?: Omit<UseAppTitleOptions, 'restoreOnUnmount'>) => {
      updateTitle(o?.title ?? DEFAULT_DOC_TITLE);
      updateUnionTitleStyle(o);
    },
    [updateTitle, updateUnionTitleStyle],
  );

  return updateAll;
};

export default useAppTitle;
